import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { GoogleMap, InfoWindow, Marker, MarkerClusterer, Polyline, useJsApiLoader } from "@react-google-maps/api";
import React, { useCallback, useEffect, useState } from "react";
import assetsCluster from "../../assets/AssetsCluster.svg";
import { getBatteryIcon } from "../../lib/getBatteryIcon";
import { getGoogleMapsApiKey } from "../../lib/getEnvVariables";
import { getCESTLocalTime } from "../../pages/Projects/ProjectDetails.helper";
import { formatTimestampWithTimezone } from "../../shared/timestampHelpers";
import "./GoogleMaps.css";
var Time;
(function (Time) {
    Time[Time["milli_Sec_500"] = 500] = "milli_Sec_500";
})(Time || (Time = {}));
const DENS_MAKE = "DENS";
/**
 * Format asset name for display on map
 * - For DENS assets: always show exactly 4 characters (first 2 + last 2)
 * - For other assets: show full name
 */
const formatAssetNameForMap = (assetName, assetMetadata) => {
    if (assetMetadata?.make === DENS_MAKE && assetName && assetName.length >= 4) {
        const truncated = assetName.substring(0, 2) + assetName.substring(assetName.length - 2);
        return truncated;
    }
    if (assetMetadata?.make === DENS_MAKE && assetName && assetName.length < 4) {
        return assetName;
    }
    return assetName;
};
const CustomGoogleMap = ({ addressDetails, addressDirections = [], mapCenter, selectedAddress, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
siteOffset: _siteOffset, siteTimezone }) => {
    const [map, setMap] = useState(null);
    const [mapType, setMapType] = useState("");
    const [infoWindowOpen, setInfoWindowOpen] = useState(null);
    const [hoveredMarker, setHoveredMarker] = useState(null);
    const [mapDelay, setMapDelay] = useState(false);
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: getGoogleMapsApiKey()
    });
    const [isSatellite, setIsSatellite] = useState(false);
    const isOpsPlannerMap = !!addressDirections?.length;
    const [tooltip, setTooltip] = useState(null);
    const onLoad = useCallback(function callback(map) {
        map.addListener("maptypeid_changed", () => {
            const currentType = map.getMapTypeId();
            setIsSatellite(currentType === "hybrid");
        });
        const bounds = new google.maps.LatLngBounds();
        addressDetails.forEach((details) => {
            bounds.extend({
                lat: Number(details.location[0]),
                lng: Number(details.location[1])
            });
        });
        map.fitBounds(bounds);
        setMap(map);
    }, [addressDetails]);
    useEffect(() => {
        const timer = setTimeout(() => {
            setMapDelay(true);
        }, Time.milli_Sec_500);
        return () => clearTimeout(timer);
    }, []);
    const getBoundsZoomLevel = (bounds, mapDim) => {
        const WORLD_DIM = { height: 256, width: 256 };
        const ZOOM_MAX = 21;
        function latRad(lat) {
            const ONE_HUNDRED_EIGHTY = 180;
            const sin = Math.sin((lat * Math.PI) / ONE_HUNDRED_EIGHTY);
            const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
            return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
        }
        function zoom(mapPx, worldPx, fraction) {
            return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
        }
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
        const lngDiff = ne.lng() - sw.lng();
        const THREE_HUNDRED_SIXTY = 360;
        const lngFraction = (lngDiff < 0 ? lngDiff + THREE_HUNDRED_SIXTY : lngDiff) / THREE_HUNDRED_SIXTY;
        const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
        const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);
        return Math.min(latZoom, lngZoom, ZOOM_MAX);
    };
    useEffect(() => {
        let zoomChangeListener;
        if (map) {
            const handleZoomChange = () => {
                setInfoWindowOpen(null);
                setHoveredMarker(null);
            };
            zoomChangeListener = map.addListener("zoom_changed", handleZoomChange);
            return () => {
                google.maps.event.removeListener(zoomChangeListener);
            };
        }
    }, [map]);
    useEffect(() => {
        if (map && selectedAddress && selectedAddress.addressList) {
            const { addressList = [] } = selectedAddress;
            if (addressList.length > 0) {
                const bounds = new google.maps.LatLngBounds();
                addressDetails.forEach((details) => {
                    if (addressList.indexOf(details.assetId) >= 0) {
                        bounds.extend({
                            lat: Number(details.location[0]),
                            lng: Number(details.location[1])
                        });
                    }
                });
                const mapElement = document.getElementById("google-map");
                const mapDim = {
                    height: mapElement?.offsetHeight,
                    width: mapElement?.offsetWidth
                };
                const zoom = addressList.length === 1
                    ? getBoundsZoomLevel(bounds, mapDim)
                    : getBoundsZoomLevel(bounds, mapDim) - 1;
                if (zoom) {
                    map.setZoom(zoom);
                    map.setCenter(bounds.getCenter());
                }
            }
            else {
                map.setZoom(8);
                map.panTo(mapCenter);
            }
        }
    }, [selectedAddress, map, mapCenter]);
    const renderMarkers = (clusterer) => addressDetails.map((details, index) => (_jsx(Marker, { clusterer: clusterer, "data-test": `marker-${index + 1}`, icon: {
            ...(isOpsPlannerMap && {
                anchor: new window.google.maps.Point(20, 40)
            }),
            labelOrigin: new window.google.maps.Point(30, 25),
            scaledSize: new window.google.maps.Size(60, 60),
            url: details.soc === undefined ? "../../assets/ChargingStationMapIcon.svg" : details.iconUrl
        }, label: {
            className: "gm-map-marker-label",
            color: isSatellite ? "white" : "black",
            fontSize: "16px",
            fontWeight: "bold",
            text: formatAssetNameForMap(details.assetName, details.assetMetadata)
        }, onMouseOut: () => {
            setHoveredMarker(null);
        }, onMouseOver: () => {
            setHoveredMarker(details);
        }, position: {
            lat: details.location[0],
            lng: details.location[1]
        } }, index)));
    useEffect(() => {
        let timer;
        const ONE_HUNDRED_FIFTY = 150;
        if (hoveredMarker) {
            timer = setTimeout(() => {
                setInfoWindowOpen(hoveredMarker);
            }, ONE_HUNDRED_FIFTY);
        }
        else {
            timer = setTimeout(() => {
                setInfoWindowOpen(null);
            }, ONE_HUNDRED_FIFTY);
        }
        return () => clearTimeout(timer);
    }, [hoveredMarker]);
    const onMapTypeIdChangeHandler = () => {
        setMapType(map?.getMapTypeId());
    };
    const getDirectionRouteColor = (routeIndex) => {
        let colorName = "", zIndex = 0, strokeWeight = 6;
        switch (routeIndex) {
            case 0:
                colorName = "#0366ff";
                zIndex = 1;
                strokeWeight = 6;
                break;
            case 1:
                colorName = "#FB5A00";
                break;
            case 2:
                colorName = "#FB5A00";
                break;
            default:
                colorName = "#FB5A00";
        }
        if (routeIndex === 1 || routeIndex > 2) {
            return {
                geodesic: true,
                icons: [
                    {
                        icon: {
                            path: "M 0,-2 0,2",
                            scale: 2,
                            strokeOpacity: 1,
                            strokeWeight: 6
                        },
                        offset: "100%",
                        repeat: "20px"
                    }
                ],
                strokeColor: "#FB5A00",
                strokeOpacity: 0.2,
                strokeWeight: 6
            };
        }
        else {
            return {
                strokeColor: colorName,
                strokeOpacity: 1,
                strokeWeight,
                zIndex
            };
        }
    };
    const getFormattedTooltip = (tooltipContent) => {
        const { addressDetail = null, addressDirection = null } = tooltipContent || {};
        if (addressDetail) {
            let tooltipList = addressDirections.filter((obj) => addressDetail.assetName === obj.srcAssetName || addressDetail.assetName === obj.destAssetName);
            tooltipList =
                tooltipList.length === 0
                    ? addressDirections.filter((obj) => obj.destAssetName === addressDetail.assetName)
                    : tooltipList;
            const updatedTooltipList = tooltipList.map((item) => {
                const { destAssetName = "", srcAssetName = "", srcAssetNameFull = "", srcAssetNameToOthers = false } = item;
                if (!srcAssetNameToOthers &&
                    addressDetail.assetName !== srcAssetNameFull &&
                    destAssetName === "WattHub") {
                    return {
                        ...item,
                        destAssetName: srcAssetName,
                        srcAssetName: srcAssetNameFull
                    };
                }
                else {
                    return item;
                }
            });
            return (_jsx("div", { "data-test": "info-window-asset-name-label", children: updatedTooltipList.map((item, index) => {
                    const { destAssetName = "", srcAssetName = "" } = item;
                    // Use nullish coalescing to handle 0 values
                    const distance = item.distance ?? "";
                    const duration = item.duration ?? "";
                    return (_jsxs("div", { className: "mb-2", children: [_jsx("div", { children: `From ${srcAssetName} to ${destAssetName}` }), _jsx("span", { children: `${distance !== "" ? distance : 0} km ` }), _jsxs("span", { children: [`/ ${duration !== "" ? duration : 0}`, typeof duration === "number" || !String(duration).toLowerCase().includes("minute")
                                        ? " minutes"
                                        : ""] })] }, `info-window-${index}`));
                }) }));
        }
        else if (addressDirection) {
            const { distance = "", duration = "", srcAssetNameFull = "", srcAssetNameToOthers = false } = addressDirection;
            let { destAssetName = "", srcAssetName = "" } = addressDirection;
            if (!srcAssetNameToOthers && destAssetName === "WattHub") {
                destAssetName = srcAssetName;
                srcAssetName = srcAssetNameFull;
            }
            return (_jsxs("div", { "data-test": "info-window-asset-name-label", children: [_jsx("div", { children: `From ${srcAssetName} to ${destAssetName}` }), _jsx("span", { children: `${distance !== "" ? distance : 0} km ` }), _jsxs("span", { children: [`/ ${duration !== "" ? duration : 0}`, typeof duration === "number" || (typeof duration === "string" && !/min/i.test(duration))
                                ? " minutes"
                                : ""] })] }));
        }
    };
    if (loadError)
        return _jsx("div", { "data-test": "loading_map_state", children: "Error..." });
    if (!isLoaded)
        return _jsx("div", { "data-test": "loading_map_state", children: "Loading..." });
    return (_jsx("div", { "data-test": "custom-google-map", children: mapDelay && (_jsx(GoogleMap, { center: mapCenter, id: "google-map", mapContainerClassName: "w-full h-[562px]", onLoad: onLoad, onMapTypeIdChanged: onMapTypeIdChangeHandler, options: {
                fullscreenControl: true,
                gestureHandling: "greedy",
                mapTypeControlOptions: { position: google.maps.ControlPosition.TOP_LEFT },
                mapTypeId: mapType || google.maps.MapTypeId?.ROADMAP,
                scrollwheel: true,
                streetViewControl: false,
                zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM }
            }, zoom: 8, children: isOpsPlannerMap ? (_jsxs(_Fragment, { children: [addressDirections.map((route, index) => {
                        const { distance = 0 } = route;
                        if (distance < 1) {
                            return "";
                        }
                        return (_jsx(Polyline, { onMouseOut: () => setTooltip(null), onMouseOver: (e) => setTooltip({
                                lat: e.latLng.lat(),
                                lng: e.latLng.lng(),
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                text: route
                            }), options: getDirectionRouteColor(index), 
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            path: route.path }, `route-${index}`));
                    }), tooltip && (_jsx(InfoWindow, { onCloseClick: () => setTooltip(null), position: { lat: tooltip.lat, lng: tooltip.lng }, children: _jsx("div", { className: "pt-4", children: getFormattedTooltip({ addressDirection: tooltip.text }) }) })), _jsx("div", { children: renderMarkers(null) }), infoWindowOpen && (_jsx(InfoWindow, { onCloseClick: () => setInfoWindowOpen(null), options: {
                            pixelOffset: new window.google.maps.Size(0, -50)
                        }, position: {
                            lat: infoWindowOpen.location[0],
                            lng: infoWindowOpen.location[1]
                        }, children: _jsx("div", { className: "pt-4", children: getFormattedTooltip({ addressDetail: infoWindowOpen }) }) }))] })) : (_jsxs(_Fragment, { children: [_jsx(MarkerClusterer, { maxZoom: mapType === "roadmap" ? 22 : 19, styles: [
                            {
                                height: 53,
                                textColor: "white",
                                url: assetsCluster,
                                width: 53
                            }
                        ], children: (clusterer) => _jsx("div", { children: renderMarkers(clusterer) }) }), infoWindowOpen && (_jsx(InfoWindow, { onCloseClick: () => setInfoWindowOpen(null), options: {
                            pixelOffset: new window.google.maps.Size(0, -50)
                        }, position: {
                            lat: infoWindowOpen.location[0],
                            lng: infoWindowOpen.location[1]
                        }, children: _jsxs("div", { className: "text-sm pt-4", children: [infoWindowOpen.assetName && (_jsxs("div", { children: [_jsxs("span", { className: "font-bold text-base", "data-test": "info-window-asset-name-label", children: ["Name :", " "] }), _jsx("span", { "data-test": "info-window-asset-name", children: formatAssetNameForMap(infoWindowOpen.assetName, infoWindowOpen.assetMetadata) })] })), infoWindowOpen.soc && (_jsxs("div", { className: "flex items-center", children: [_jsx("span", { className: "font-bold", "data-test": "soc", children: "SOC :" }), _jsxs("span", { className: "ml-1", "data-test": "soc-value", children: [Math.round(infoWindowOpen.soc), "%\u00A0"] }), getBatteryIcon(Number(infoWindowOpen?.soc))] })), infoWindowOpen?.timestamp && (_jsxs("div", { children: [_jsxs("span", { className: "font-bold", "data-test": "last-reported-time", children: ["Last Reported Time :", " "] }), infoWindowOpen.timestamp &&
                                            (() => {
                                                // Use shared helper to format timestamps (handles both ISO and time-only formats)
                                                const { formattedTime, timezoneAbbr } = formatTimestampWithTimezone(infoWindowOpen.timestamp, siteTimezone, "iso-date", // Format: "yyyy-MM-dd HH:mm"
                                                (ts) => getCESTLocalTime(ts, "yyyy-MM-dd HH:mm"));
                                                return (_jsxs("span", { "data-test": "last-reported-time-zone-value", children: [formattedTime, " ", timezoneAbbr] }));
                                            })()] }))] }) }))] })) })) }));
};
export default CustomGoogleMap;
